// React and routing imports
import React, {FC} from 'react';
import {Outlet} from "react-router-dom"; // For rendering nested routes
import {Link} from 'react-router-dom'; // For navigation links

// Material-UI component imports
import {Box, Divider, Typography} from "@mui/material";
import {styled} from "@mui/material/styles";
import MenuIcon from '@mui/icons-material/Menu';
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Drawer from "@mui/material/Drawer";
import MuiAppBar, {AppBarProps as MuiAppBarProps} from '@mui/material/AppBar';
import Toolbar from "@mui/material/Toolbar";

// Package version for display in header
import packageJson from '../../package.json';


// Props interface for the Layout component (currently empty)
interface LayoutProps {
}

// Navigation menu items configuration
// Each item has a title (displayed text) and a route path (to)
const navigation = [
  {title: "Facturee MUI Elements", to: "/"},
  {title: "Component Accordion", to: "component-example-accordion"},
  {title: "Component Numeric Input", to: "component-example-numeric-input"},
  {title: "Component FileUpload", to: "component-example-upload"},
  {title: "Component Dialog Modal", to: "component-example-modal-dialog"},
  {title: "Component Info Modal", to: "component-example-modal-info"},
  {title: "Component Paper", to: "component-example-paper"},
  {title: "Component Shortlist", to: "component-example-shortlist"},
  {title: "Component Stepper", to: "component-example-stepper"},
  {title: "Component AI Loading Icon", to: "component-example-ai-loading-icon"},
];

// Drawer width constant (in pixels)
const drawerWidth = 240;

// Extended AppBar props to include 'open' state
interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

// Styled AppBar component
// Prevents 'open' prop from being forwarded to the DOM element
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({theme, open}) => ({
  width: '100%', // Full width regardless of drawer state
}));

// Styled main content area
// Prevents 'open' prop from being forwarded to the DOM element
const Main = styled('main', {shouldForwardProp: (prop) => prop !== 'open'})<{
  open?: boolean;
}>(({theme, open}) => ({
  flexGrow: 1, // Takes up remaining space in flex container
  width: '100%', // Full width
}));

// Styled drawer header component
// Provides spacing and alignment for the drawer's top section
const DrawerHeader = styled('div')(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1), // Horizontal padding
  ...theme.mixins.toolbar, // Ensures proper height to match the AppBar
  justifyContent: 'flex-end', // Aligns content to the right
}));

// Layout component
const Layout: FC<LayoutProps> = () => {
  // State to control drawer open/closed status
  const [open, setOpen] = React.useState(false);

  // Toggle function to open/close the drawer
  const toggleDrawer = () => {
    setOpen(!open);
  };

  return (
    // Main container with flex layout and hidden horizontal overflow
    <Box sx={{display: 'flex', overflowX: 'hidden'}}>
      {/* Fixed top navigation bar */}
      <AppBar position="fixed" open={open}>
        <Toolbar>
          {/* Menu button to toggle drawer */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            edge="start"
            sx={{mr: 2}} // Right margin
          >
            <MenuIcon/>
          </IconButton>
          {/* Application title with version number */}
          <Typography variant="h6" noWrap component="div">
            Facturee MUI Elements & Custom Components – Version {packageJson.version}
          </Typography>
        </Toolbar>
      </AppBar>
      {/* Side navigation drawer */}
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0, // Prevents drawer from shrinking
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant="temporary" // Drawer overlays content and can be closed
        anchor="left" // Drawer appears from the left side
        open={open} // Controlled by state
        onClose={toggleDrawer} // Close when clicking outside
      >
        {/* Header section with close button */}
        <DrawerHeader>
          <IconButton 
            onClick={toggleDrawer}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 152, 0, 0.1)', // Light orange hover
              }
            }}
          >
            <Typography variant="h6" color="secondary.main">✕</Typography>
          </IconButton>
        </DrawerHeader>
        <Divider/>
        {/* Navigation menu list */}
        <List>
          {/* Map through navigation items and create menu entries */}
          {navigation.map((item, index) => (
            <ListItem disablePadding key={index}>
              {/* Navigation button that uses React Router Link */}
              <ListItemButton
                component={Link}
                to={item.to}
              >
                <ListItemText primary={item.title}/>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
      {/* Main content area where nested routes are rendered */}
      <Main open={open}>
        {/* Outlet renders the matched child route component */}
        <Outlet/>
      </Main>
    </Box>
  )
};

export default Layout;
